import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  FileText, 
  Building, 
  MapPin, 
  BarChart3, 
  Users, 
  Settings,
  History,
  Trash2,
  UserCircle,
  Leaf,
  CheckCircle,
  Briefcase,
  Phone,
  User,
  UserCheck,
  Award,
  GraduationCap,
  AlertTriangle,
  FolderOpen,
  Sparkles,
  Bell,
  LifeBuoy,
  Shield,
  Mail,
  Send,
  ClipboardList,
  Inbox,
  ScrollText,
} from 'lucide-react';
import { useRouteAccess } from '../../hooks/useRouteAccess';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { isSuperAdminUser, isPlatformOnlySuperAdmin } from '../../utils/platformRoles';
import { PLATFORM_ADMIN_PATH } from '../../constants/navigation';
import { useTenant } from '../../contexts/TenantContext';
import { isConsultoriaTenant } from '../../utils/tenantProfile';
import { TenantCoatMark, PoweredByUseAmbiental } from '../Brand/TenantBrandMark';
import { tenantBrand } from '../../utils/tenantBranding';

function NavSection({ title, items, isActive, onNavigate }) {
  if (!items.length) return null;
  return (
    <div>
      <h3 className="text-xs font-semibold text-primary-300 uppercase tracking-wider mb-3">
        {title}
      </h3>
      <ul className="space-y-2">
        {items.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <li key={item.path}>
              <Link
                to={item.path}
                data-tour={`nav:${item.path}`}
                aria-current={active ? 'page' : undefined}
                onClick={onNavigate}
                className={`nav-item group ${
                  active
                    ? 'active'
                    : 'text-primary-200 hover:bg-primary-700/80 hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5" />
                <div className="flex-1">
                  <div className="font-medium">{item.label}</div>
                  <div className="text-xs opacity-75">{item.description}</div>
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const Sidebar = ({ open = false, onClose }) => {
  const { t } = useTranslation(['nav', 'common']);
  const location = useLocation();
  const { user } = useAuth();
  const { tenant } = useTenant();
  const { canAccessRoute } = useRouteAccess();
  const showSuperAdmin = isSuperAdminUser(user);
  const platformOnly = isPlatformOnlySuperAdmin(user);
  const consultoria = isConsultoriaTenant(tenant);
  const pick = (items) => {
    if (platformOnly) return [];
    return items.filter((item) => canAccessRoute(item.path));
  };

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const hojeItems = pick([
    { path: '/pedidos', icon: Inbox, label: 'Pedidos', description: 'Solicitar algo ao órgão' },
    { path: '/', icon: Home, label: t('dashboard'), description: t('dashboardDesc') },
    { path: '/me', icon: ClipboardList, label: t('myTasks'), description: t('myTasksDesc') },
    { path: '/assistant', icon: Sparkles, label: t('assistant'), description: t('assistantDesc') },
    { path: '/notifications', icon: Bell, label: t('notifications'), description: t('notificationsDesc') },
    { path: '/support', icon: LifeBuoy, label: 'Suporte', description: 'Chamados do sistema' },
  ]);

  const routingItems = pick([
    { path: '/process/routing', icon: Inbox, label: t('processRouting'), description: t('processRoutingDesc') },
  ]);

  const carteiraItems = pick([
    { path: '/portfolio', icon: Briefcase, label: t('carteira'), description: t('carteiraDesc') },
  ]);

  const protocolosItems = pick([
    { path: '/process/cases', icon: FileText, label: t('processos'), description: t('processosDesc') },
    { path: '/requerimentos', icon: ClipboardList, label: t('requerimentos'), description: t('requerimentosDesc') },
    { path: '/pedidos', icon: Inbox, label: 'Pedidos', description: 'Solicitações diversas do requerente' },
    { path: '/files', icon: FolderOpen, label: t('files', { defaultValue: 'Arquivos' }), description: t('filesDesc', { defaultValue: 'Visualizar e baixar anexos' }) },
    {
      path: '/portal',
      icon: Building,
      label: consultoria ? t('portalClient') : t('portal'),
      description: consultoria ? t('portalClientDesc') : t('portalDesc'),
    },
  ]);

  const licenciamentoItems = pick([
    { path: '/licenses', icon: FileText, label: t('licenses'), description: t('licensesDesc') },
    { path: '/files', icon: FolderOpen, label: t('files'), description: t('filesDesc') },
    { path: '/enterprises', icon: Building, label: t('enterprises'), description: t('enterprisesDesc') },
    { path: '/process/cases', icon: FileText, label: t('processos'), description: t('processosDesc') },
    { path: '/requerimentos', icon: ClipboardList, label: t('requerimentos'), description: t('requerimentosDesc') },
    { path: '/pedidos', icon: Inbox, label: 'Pedidos', description: 'Solicitações diversas do requerente' },
    { path: '/license/types', icon: FileText, label: t('licenseTypes'), description: t('licenseTypesDesc') },
    { path: '/license/conditions', icon: ClipboardList, label: t('conditions'), description: t('conditionsDesc') },
    { path: '/licenses/assisted', icon: Sparkles, label: t('assistedLicense'), description: t('assistedLicenseDesc') },
    { path: '/license/submodules', icon: FileText, label: t('licenseSubmodules'), description: t('licenseSubmodulesDesc') },
    { path: '/license/regularization-plans', icon: ClipboardList, label: t('regularizationPlan'), description: t('regularizationPlanDesc') },
    { path: '/legislation', icon: ScrollText, label: t('legislation'), description: t('legislationDesc') },
    {
      path: '/portal',
      icon: Building,
      label: consultoria ? t('portalClient') : t('portal'),
      description: consultoria ? t('portalClientDesc') : t('portalDesc'),
    },
    { path: '/process/media-types', icon: FolderOpen, label: t('mediaTypes'), description: t('mediaTypesDesc') },
  ]);

  const tecnicoItems = pick([
    { path: '/licenses', icon: FileText, label: t('licenses'), description: t('licensesDesc') },
    { path: '/files', icon: FolderOpen, label: t('files'), description: t('filesDesc') },
    { path: '/enterprises', icon: Building, label: t('enterprises'), description: t('enterprisesDesc') },
    { path: '/license/types', icon: FileText, label: t('licenseTypes'), description: t('licenseTypesDesc') },
    { path: '/license/conditions', icon: ClipboardList, label: t('conditions'), description: t('conditionsDesc') },
    { path: '/licenses/assisted', icon: Sparkles, label: t('assistedLicense'), description: t('assistedLicenseDesc') },
    { path: '/license/submodules', icon: FileText, label: t('licenseSubmodules'), description: t('licenseSubmodulesDesc') },
    { path: '/license/regularization-plans', icon: ClipboardList, label: t('regularizationPlan'), description: t('regularizationPlanDesc') },
    { path: '/legislation', icon: ScrollText, label: t('legislation'), description: t('legislationDesc') },
    { path: '/map', icon: MapPin, label: t('map'), description: t('mapDesc') },
    { path: '/reports', icon: BarChart3, label: t('reports'), description: t('reportsDesc') },
    { path: '/process/media-types', icon: FolderOpen, label: t('mediaTypes'), description: t('mediaTypesDesc') },
  ]);

  const campoItems = pick([
    { path: '/map', icon: MapPin, label: t('map'), description: t('mapDesc') },
    { path: '/reports', icon: BarChart3, label: t('reports'), description: t('reportsDesc') },
    { path: '/complaint/complaints', icon: AlertTriangle, label: t('complaintsItem'), description: t('complaintsItemDesc') },
    { path: '/complaint/categories', icon: FolderOpen, label: t('complaintCategories'), description: t('complaintCategoriesDesc') },
    { path: '/inspections', icon: AlertTriangle, label: t('fiscalizacao'), description: t('fiscalizacaoDesc') },
  ]);

  const catalogItems = [
    { path: '/catalog/company-sizes', icon: Building, label: t('companySizes'), description: t('companySizesDesc') },
    { path: '/catalog/company-statuses', icon: CheckCircle, label: t('companyStatuses'), description: t('companyStatusesDesc') },
    { path: '/catalog/economic-activities', icon: Briefcase, label: t('economicActivities'), description: t('economicActivitiesDesc') },
  ];

  const contactItems = [
    { path: '/contact/contacts', icon: Phone, label: t('contactsItem'), description: t('contactsItemDesc') },
    { path: '/contact/positions', icon: Briefcase, label: t('positions'), description: t('positionsDesc') },
  ];

  const identityItems = [
    { path: '/identity/persons', icon: User, label: t('persons'), description: t('personsDesc') },
    { path: '/identity/companies', icon: Building, label: t('companies'), description: t('companiesDesc') },
    {
      path: '/identity/clients',
      icon: Users,
      label: consultoria ? t('clients') : t('requesters'),
      description: consultoria ? t('clientsDesc') : t('requestersDesc'),
    },
  ];

  const professionalItems = [
    { path: '/professional/technical-responsibles', icon: UserCheck, label: t('technicalResponsibles'), description: t('technicalResponsiblesDesc') },
    { path: '/professional/professional-councils', icon: Award, label: t('councils'), description: t('councilsDesc') },
    { path: '/professional/professional-formations', icon: GraduationCap, label: t('formations'), description: t('formationsDesc') },
  ];

  const institutionItems = [
    { path: '/institution/agencies', icon: Building, label: t('orgaos'), description: t('orgaosDesc') },
    { path: '/institution/departments', icon: Briefcase, label: t('setores'), description: t('setoresDesc') },
    { path: '/institution/job-titles', icon: Award, label: t('cargos'), description: t('cargosDesc') },
    { path: '/institution/staff', icon: UserCheck, label: t('servidores'), description: t('servidoresDesc') },
  ];

  const adminCoreItems = [
    { path: '/users', icon: Users, label: t('users'), description: t('usersDesc') },
    { path: '/history', icon: History, label: t('history'), description: t('historyDesc') },
    { path: '/trash', icon: Trash2, label: t('trash'), description: t('trashDesc') },
    { path: '/profile', icon: UserCircle, label: t('profile'), description: t('profileDesc') },
    { path: '/settings', icon: Settings, label: t('settings'), description: t('settingsDesc') },
  ];

  const notificationManageItems = [
    { path: '/notifications/templates', icon: Mail, label: t('templates'), description: t('templatesDesc') },
    { path: '/notifications/send', icon: Send, label: t('sendNotification'), description: t('sendNotificationDesc') },
  ];

  const adminItems = pick([
    ...adminCoreItems,
    ...catalogItems,
    ...contactItems,
    ...identityItems,
    ...professionalItems,
    ...(consultoria ? [] : institutionItems),
    ...notificationManageItems,
  ]);

  const profileItem = adminCoreItems.find((item) => item.path === '/profile');
  const sections = consultoria
    ? [
        { title: t('hoje'), items: hojeItems },
        { title: t('carteira'), items: carteiraItems },
        { title: t('protocolos'), items: protocolosItems },
        { title: t('tecnico'), items: tecnicoItems },
        { title: t('adminRecords'), items: adminItems },
      ]
    : [
        { title: t('hoje'), items: hojeItems },
        { title: t('tramitacaoSection'), items: routingItems },
        { title: t('licenciamento'), items: licenciamentoItems },
        { title: t('campo'), items: campoItems },
        { title: t('adminRecords'), items: adminItems },
      ];

  const superAdminNav = showSuperAdmin
    ? [
        {
          path: PLATFORM_ADMIN_PATH,
          icon: Shield,
          label: t('platformAdmin'),
          description: t('platformAdminDesc'),
        },
        {
          path: '/admin/email-providers',
          icon: Mail,
          label: t('emailProviders'),
          description: t('emailProvidersDesc'),
        },
        {
          path: '/admin/email-templates',
          icon: FileText,
          label: t('emailTemplates'),
          description: t('emailTemplatesDesc'),
        },
      ].filter((item) => canAccessRoute(item.path))
    : [];

  const platformOnlyNav =
    platformOnly && profileItem && canAccessRoute('/profile')
      ? [{ ...profileItem }]
      : [];
  const brand = tenantBrand(tenant);

  return (
    <div
      className={`sidebar fixed inset-y-0 left-0 z-50 h-full w-[min(18rem,88vw)] shrink-0 overflow-y-auto bg-gradient-to-b from-primary-800 to-primary-900 text-white transition-transform duration-200 lg:static lg:z-auto lg:w-72 lg:translate-x-0 ${
        open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}
    >
      <div className="sidebar-header">
        {platformOnly ? (
          <div className="logo">
            <Leaf className="logo-icon" />
            <div>
              <h1 className="logo-text">{t('common:brandName')}</h1>
              <p className="text-sm text-primary-200">{t('common:platformConsole')}</p>
            </div>
          </div>
        ) : brand.coatOfArmsUrl || brand.logoUrl ? (
          <div className="flex w-full flex-col items-center text-center">
            <TenantCoatMark
              tenant={tenant}
              size={240}
              square={false}
              className="w-full max-w-full"
              fallback={false}
            />
            <p className="mt-3 text-xs font-medium leading-snug text-primary-100/90">
              {t('common:systemName')}
            </p>
            <PoweredByUseAmbiental className="mt-1.5" light />
          </div>
        ) : (
          <div className="logo">
            <Leaf className="logo-icon" />
            <div>
              <h1 className="logo-text">{tenant?.name || t('common:brandName')}</h1>
              <p className="text-sm text-primary-200">{t('common:systemName')}</p>
              <PoweredByUseAmbiental className="mt-1" light />
            </div>
          </div>
        )}
      </div>
      
      <nav className="p-4 space-y-6">
        {superAdminNav.length > 0 && (
          <NavSection
            title={platformOnly ? t('menu') : t('platform')}
            items={superAdminNav}
            isActive={isActive}
            onNavigate={onClose}
          />
        )}
        {platformOnly ? (
          <NavSection title={t('accountSection')} items={platformOnlyNav} isActive={isActive} onNavigate={onClose} />
        ) : (
          sections.map((section) => (
            <NavSection
              key={section.title}
              title={section.title}
              items={section.items}
              isActive={isActive}
              onNavigate={onClose}
            />
          ))
        )}
      </nav>
      
      <div className="mt-auto p-4 border-t border-primary-700">
        <a href="/docs/" className="mb-3 inline-flex text-sm font-medium text-white hover:underline">
          Manual de procedimentos
        </a>
        <div className="text-xs text-primary-300">
          <p>{t('common:versionLabel', { version: '1.0.0' })}</p>
          <p>{t('common:copyright')}</p>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
